import { useTranslation } from 'react-i18next'
import ManufacturerLogo from '../ManufacturerLogo'
import ListingNoPhotoPlaceholder from '../ListingNoPhotoPlaceholder'
import ListingDetailPhotoCarousel from '../ListingDetailPhotoCarousel'
import { publicAssetUrl } from '../../utils/assetUrl'
import type { ListingPreviewViewModel } from '../../utils/mapDraftToListingPreview'

type Props = {
  viewModel: ListingPreviewViewModel
  /** Sync carousel / thumbs with builder cover photo index. */
  interactiveThumbs?: boolean
  onThumbSelect?: (index: number) => void
  /** @deprecated All photos show in the thumb strip like live listing detail. */
  onOverflowThumbSelect?: () => void
  className?: string
}

export default function ListingPreview({
  viewModel,
  interactiveThumbs = false,
  onThumbSelect,
  className = '',
}: Props) {
  const { t } = useTranslation()
  const {
    manufacturer,
    modelLabel,
    priceLabel,
    storageLabel,
    yearLabel,
    batteryLabel,
    conditionLabel,
    inBoxLabel,
    warrantyLabel,
    carouselPhotos,
    activePhotoIndex,
    sellerName,
    sellerNameSub,
    cityLabel,
    cityValue,
    showCompletionHint,
  } = viewModel

  const photoCount = carouselPhotos.length
  const safeActivePhoto =
    photoCount === 0 ? 0 : Math.min(Math.max(0, activePhotoIndex), photoCount - 1)

  function handleActivePhotoChange(index: number) {
    onThumbSelect?.(index)
  }

  return (
    <aside
      className={`listing-preview listing-builder-preview${className ? ` ${className}` : ''}`}
      aria-label={t('listing.builder.previewTitle')}
    >
      <header className="listing-builder-preview__head">
        <h3 className="listing-builder-preview__title">{t('listing.builder.previewTitle')}</h3>
        <span className="listing-builder-preview__badge">{t('listing.builder.previewLive')}</span>
      </header>

      <div className="listing-builder-preview__card">
        <div className="listing-builder-preview__hero">
          <div className="listing-builder-preview__hero-left">
            <div className="listing-detail-gallery listing-detail-gallery--user listing-builder-preview__gallery">
              {photoCount > 0 ? (
                <>
                  <ListingDetailPhotoCarousel
                    photos={carouselPhotos}
                    activePhoto={safeActivePhoto}
                    onActivePhotoChange={handleActivePhotoChange}
                    staticPreview={!interactiveThumbs}
                  />
                  {photoCount > 1 ? (
                    <div
                      className="listing-detail-thumbs"
                      role="tablist"
                      aria-label={t('listing.detailGallery')}
                    >
                      {carouselPhotos.map((img, idx) => (
                        <button
                          key={img.id}
                          type="button"
                          role="tab"
                          aria-selected={idx === safeActivePhoto}
                          className={`listing-detail-thumb${
                            idx === safeActivePhoto ? ' listing-detail-thumb--active' : ''
                          }`}
                          tabIndex={interactiveThumbs ? 0 : -1}
                          onClick={() => interactiveThumbs && onThumbSelect?.(idx)}
                        >
                          <img
                            src={publicAssetUrl(img.display_url || img.thumbnail_url)}
                            alt=""
                            draggable={false}
                          />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="listing-detail-main-photo">
                  <ListingNoPhotoPlaceholder variant="detail" className="listing-no-photo--builder-preview" />
                </div>
              )}
            </div>
          </div>
          <div className="listing-builder-preview__hero-meta">
            <div className="listing-builder-preview__hero-actions">
              <button
                type="button"
                className="listing-builder-preview__icon-btn"
                aria-label={t('vc.tab.favorites')}
                tabIndex={-1}
              >
                ♥
              </button>
              <button type="button" className="listing-builder-preview__icon-btn" tabIndex={-1} aria-hidden>
                ↗
              </button>
            </div>
            <span className="listing-builder-preview__brand-row">
              {manufacturer ? (
                <>
                  <ManufacturerLogo manufacturer={manufacturer} size={20} />
                  <span>{manufacturer}</span>
                </>
              ) : (
                <span className="listing-builder-preview__brand-placeholder">—</span>
              )}
            </span>
            <p className="listing-builder-preview__model">{modelLabel}</p>
            <p className="listing-builder-preview__price">{priceLabel}</p>
            <ul className="listing-builder-preview__chips listing-builder-preview__chips--hero">
              <li>{storageLabel}</li>
              <li>{yearLabel}</li>
              <li>{batteryLabel}</li>
            </ul>
          </div>
        </div>

        <dl className="listing-builder-preview__rows">
          <div>
            <dt>{t('listing.detailCondition')}</dt>
            <dd>{conditionLabel}</dd>
          </div>
          <div>
            <dt>{t('listing.detailKit')}</dt>
            <dd>{inBoxLabel}</dd>
          </div>
          <div>
            <dt>{t('listing.builder.previewWarranty')}</dt>
            <dd>{warrantyLabel}</dd>
          </div>
        </dl>

        {showCompletionHint ? (
          <div className="listing-builder-preview__notice" role="status">
            <span className="listing-builder-preview__notice-icon" aria-hidden="true">
              ✦
            </span>
            <p>{t('listing.builder.previewStepsHint')}</p>
          </div>
        ) : null}

        <div className="listing-builder-preview__seller">
          <div className="listing-builder-preview__seller-left">
            <span className="listing-builder-preview__avatar" aria-hidden="true">
              {(sellerName || '?').charAt(0).toUpperCase()}
            </span>
            <div>
              <p className="listing-builder-preview__seller-name">{sellerName}</p>
              {sellerNameSub ? (
                <p className="listing-builder-preview__seller-sub">{sellerNameSub}</p>
              ) : null}
            </div>
          </div>
          <div className="listing-builder-preview__seller-right">
            <span className="listing-builder-preview__pin" aria-hidden="true" />
            <div>
              <p className="listing-builder-preview__seller-city-label">{cityLabel}</p>
              <p className="listing-builder-preview__seller-sub">{cityValue}</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
